import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import ExamGradingModal from '../components/ExamGradingModal';
import ExamChatVoice from '../components/ExamChatVoice';
import ExamLiveView from '../components/ExamLiveView';
import {
  Loader2, Plus, MonitorPlay, GraduationCap, FileDown, Unlock, RotateCcw,
  CheckCircle2, XCircle, Trash2, Pencil, AlertTriangle, Camera, ChevronDown, ChevronRight, ShieldCheck,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const GOLD_INK = '#8F650F';
const TERRA = '#E07A5F';

const STATUS = {
  freigeschaltet: { label: 'Freigeschaltet', color: GOLD_INK },
  laufend: { label: 'Läuft', color: TERRA },
  abgegeben: { label: 'Abgegeben', color: GOLD_INK },
  bewertet: { label: 'Bewertet', color: '#2F7A3D' },
};

const flagSum = (f) => Object.values(f || {}).reduce((a, b) => a + b, 0);

// ---------- Live-Monitor-Kachel ----------
const MonitorTile = ({ item, getAuthHeader }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const [liveView, setLiveView] = useState(false);
  const [remoteStream, setRemoteStream] = useState(null);
  const [controlsSlot, setControlsSlot] = useState(null);
  const urlRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && remoteStream) {
      videoRef.current.srcObject = remoteStream;
      videoRef.current.play().catch(() => {});
    }
  }, [remoteStream]);

  useEffect(() => {
    let alive = true;
    const fetchFrame = async () => {
      try {
        const r = await axios.get(`${API}/admin/exam-attempts/${item.attempt_id}/live-frame`,
          { ...getAuthHeader(), responseType: 'blob' });
        if (!alive) return;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = URL.createObjectURL(r.data);
        setImgUrl(urlRef.current);
      } catch { /* noch kein Bild */ }
    };
    fetchFrame();
    const t = setInterval(fetchFrame, 3000);
    return () => { alive = false; clearInterval(t); if (urlRef.current) URL.revokeObjectURL(urlRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.attempt_id]);

  const flags = flagSum(item.flags);
  const mins = Math.floor((item.remaining_seconds || 0) / 60);
  return (
    <div className="bg-cream-100 border border-cream-300 rounded-2xl overflow-hidden" data-testid={`monitor-tile-${item.attempt_id}`}>
      <div className="aspect-[4/3] bg-espresso relative">
        {remoteStream ? (
          <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" data-testid={`monitor-video-${item.attempt_id}`} />
        ) : imgUrl
          ? <img src={imgUrl} alt={`Live: ${item.user_name}`} className="w-full h-full object-cover" />
          : (
            <div className="w-full h-full flex flex-col items-center justify-center text-cream-50/50 text-[12px] gap-2">
              <Camera className="w-6 h-6" />
              {item.camera_required ? 'Warte auf Kamerabild…' : 'Kamera-Ausnahme'}
            </div>
          )}
        <span className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/60 text-cream-50 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> {remoteStream ? 'Live-Video' : 'Live'}
        </span>
        {flags > 0 && (
          <span className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded-full" data-testid={`monitor-flags-${item.attempt_id}`}>
            ⚠ {flags}
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="text-[14px] font-semibold text-espresso truncate">{item.user_name}</p>
        <p className="text-[11.5px] text-sand-600 truncate">{item.exam_title}</p>
        <p className="text-[11.5px] text-sand-500 mt-1 mb-2">Restzeit ~{mins} Min.
          {item.flags?.tab_hidden > 0 && <span className="text-red-700"> · {item.flags.tab_hidden}× Tab verlassen</span>}
        </p>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <ExamChatVoice
            attemptId={item.attempt_id}
            getAuthHeader={getAuthHeader}
            role="admin"
            userName={item.user_name}
            onRemoteStream={setRemoteStream}
            portalTarget={liveView ? controlsSlot : null}
          />
          <button
            onClick={() => setLiveView(true)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-espresso text-cream-50"
            data-testid={`monitor-liveview-btn-${item.attempt_id}`}
          >
            <MonitorPlay className="w-3 h-3" /> Prüfung ansehen
          </button>
        </div>
        {liveView && (
          <ExamLiveView
            attemptId={item.attempt_id}
            getAuthHeader={getAuthHeader}
            onClose={() => { setLiveView(false); setControlsSlot(null); }}
            videoStream={remoteStream}
            snapshotUrl={imgUrl}
            onControlsSlot={setControlsSlot}
          />
        )}
      </div>
    </div>
  );
};

// ---------- Haupt-Seite ----------
export default function AdminExamsPage({ Layout }) {
  const { getAuthHeader } = useAuth();
  const [tab, setTab] = useState('exams'); // exams | monitor
  const [exams, setExams] = useState(null);
  const [running, setRunning] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [attempts, setAttempts] = useState({});
  const [candidates, setCandidates] = useState(null); // {examId, list}
  const [builder, setBuilder] = useState(null); // exam-Objekt oder {} für neu
  const [courses, setCourses] = useState([]);
  const [gradingId, setGradingId] = useState(null); // attempt_id für Bewertungs-Modal
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/exams`, getAuthHeader());
      setExams(r.data.exams || []);
    } catch { setExams([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/courses`, getAuthHeader()).then((r) => setCourses(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Monitor-Polling
  useEffect(() => {
    const fetchMonitor = async () => {
      try {
        const r = await axios.get(`${API}/admin/exam-monitor`, getAuthHeader());
        setRunning(r.data.running || []);
      } catch { /* noop */ }
    };
    fetchMonitor();
    const t = setInterval(fetchMonitor, tab === 'monitor' ? 3000 : 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadAttempts = async (examId) => {
    try {
      const r = await axios.get(`${API}/admin/exams/${examId}/attempts`, getAuthHeader());
      setAttempts((a) => ({ ...a, [examId]: r.data.attempts || [] }));
    } catch { /* noop */ }
  };

  const toggleExpand = (examId) => {
    const next = expanded === examId ? null : examId;
    setExpanded(next);
    if (next) loadAttempts(next);
  };

  const openUnlock = async (exam) => {
    setCandidates({ examId: exam.id, list: null });
    try {
      const r = await axios.get(`${API}/admin/exams/${exam.id}/candidates`, getAuthHeader());
      setCandidates({ examId: exam.id, list: r.data.candidates || [] });
    } catch { setCandidates(null); toast.error('Teilnehmer konnten nicht geladen werden'); }
  };

  const unlock = async (userId, cameraRequired) => {
    try {
      await axios.post(`${API}/admin/exams/${candidates.examId}/unlock`,
        { user_id: userId, camera_required: cameraRequired }, getAuthHeader());
      toast.success('Prüfung freigeschaltet');
      const r = await axios.get(`${API}/admin/exams/${candidates.examId}/candidates`, getAuthHeader());
      setCandidates((c) => ({ ...c, list: r.data.candidates || [] }));
      loadAttempts(candidates.examId);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Freischalten fehlgeschlagen'); }
  };

  // Builder: Frage-Zeilen <-> API-Format
  const rowFromQuestion = (q) => ({
    id: q.id, type: q.type || 'open', text: q.text || '', points: q.points || (q.type === 'open' ? 5 : 2),
    options_text: (q.type === 'mc' || q.type === 'mc_multi')
      ? (q.options || []).map((o, i) => ((q.type === 'mc_multi' ? (q.correct_indices || []).includes(i) : i === q.correct_index) ? `*${o}` : o)).join('\n')
      : '',
    sample_solution: q.sample_solution || '',
  });

  const saveExam = async () => {
    if (!builder.course_id) { toast.error('Bitte Kurs wählen'); return; }
    const questions = [];
    for (const [idx, row] of (builder.questions || []).entries()) {
      const text = (row.text || '').trim();
      if (!text) continue;
      const q = { id: row.id, type: row.type, text, points: Number(row.points) || 1 };
      if (row.type === 'open' && (row.sample_solution || '').trim()) q.sample_solution = row.sample_solution.trim();
      if (row.type !== 'open') {
        const lines = (row.options_text || '').split('\n').map((s) => s.trim()).filter(Boolean);
        q.options = lines.map((l) => l.replace(/^\*/, '').trim());
        const starred = lines.map((l, i) => (l.startsWith('*') ? i : -1)).filter((i) => i >= 0);
        if (q.options.length < 2) { toast.error(`Frage ${idx + 1}: MC braucht mind. 2 Optionen (eine pro Zeile)`); return; }
        if (starred.length === 0) { toast.error(`Frage ${idx + 1}: richtige Option(en) mit * am Zeilenanfang markieren`); return; }
        if (starred.length > 1) { q.type = 'mc_multi'; q.correct_indices = starred; }
        else { q.type = 'mc'; q.correct_index = starred[0]; }
      }
      questions.push(q);
    }
    if (questions.length === 0) { toast.error('Mindestens eine Frage erforderlich'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/exams`, {
        id: builder.id || '',
        course_id: builder.course_id,
        title: builder.title || 'Abschlussprüfung',
        duration_minutes: Number(builder.duration_minutes) || 90,
        mc_per_attempt: Number(builder.mc_per_attempt) || 0,
        open_per_attempt: Number(builder.open_per_attempt) || 0,
        pass_percent: Number(builder.pass_percent) || 50,
        questions,
        active: builder.active !== false,
      }, getAuthHeader());
      toast.success('Prüfung gespeichert');
      setBuilder(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setBusy(false);
  };

  const seedPilot = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/exams/seed-pilot?confirm=true`, {}, getAuthHeader());
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy(false);
  };

  const allowRetry = async (att) => {
    if (!window.confirm(`Wiederholungsversuch für ${att.user_name} freigeben?`)) return;
    try {
      await axios.post(`${API}/admin/exam-attempts/${att.id}/retry`,
        { user_id: att.user_id, camera_required: att.camera_required !== false }, getAuthHeader());
      toast.success('Wiederholung freigeschaltet');
      loadAttempts(att.exam_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehlgeschlagen'); }
  };

  const deleteAttempt = async (att) => {
    if (!window.confirm(`Versuch von ${att.user_name} unwiderruflich löschen (inkl. Fotos)?`)) return;
    try {
      await axios.delete(`${API}/admin/exam-attempts/${att.id}`, getAuthHeader());
      toast.success('Versuch gelöscht');
      loadAttempts(att.exam_id);
      load();
    } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const downloadPdf = async (att) => {
    try {
      const r = await axios.get(`${API}/admin/exam-attempts/${att.id}/pdf`, { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Pruefung_${att.user_name}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF fehlgeschlagen'); }
  };

  const downloadProtocol = async (att) => {
    try {
      const r = await axios.get(`${API}/admin/exam-attempts/${att.id}/protocol-pdf`, { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Aufsichtsprotokoll_${att.user_name}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Protokoll fehlgeschlagen'); }
  };

  const content = (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <p className="font-mono text-[10px] uppercase font-semibold mb-2" style={{ letterSpacing: '0.24em', color: GOLD_INK }}>
        Admin · Prüfungsraum
      </p>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl sm:text-4xl text-espresso">Prüfungen</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setTab('exams')}
            className={`px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider ${tab === 'exams' ? 'bg-espresso text-cream-50' : 'border border-cream-400 text-sand-700'}`}
            data-testid="tab-exams"
          >
            <GraduationCap className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Prüfungen
          </button>
          <button
            onClick={() => setTab('monitor')}
            className={`px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider ${tab === 'monitor' ? 'bg-espresso text-cream-50' : 'border border-cream-400 text-sand-700'}`}
            data-testid="tab-monitor"
          >
            <MonitorPlay className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Live-Monitor
            {running.length > 0 && <span className="ml-1.5 bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{running.length}</span>}
          </button>
        </div>
      </div>

      {/* ================= LIVE-MONITOR ================= */}
      {tab === 'monitor' && (
        <div data-testid="exam-monitor">
          {running.length === 0 ? (
            <div className="bg-cream-100 border border-cream-300 rounded-2xl p-12 text-center text-sand-500">
              <MonitorPlay className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Gerade läuft keine Prüfung.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {running.map((r) => <MonitorTile key={r.attempt_id} item={r} getAuthHeader={getAuthHeader} />)}
            </div>
          )}
        </div>
      )}

      {/* ================= PRÜFUNGEN ================= */}
      {tab === 'exams' && (
        <>
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setBuilder({ duration_minutes: 90, mc_per_attempt: 25, open_per_attempt: 10, pass_percent: 50, questions: [], active: true })}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider"
              style={{ backgroundColor: TERRA, color: '#FBF7F0' }}
              data-testid="new-exam-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Neue Prüfung
            </button>
            {exams && !exams.some((e) => e.id === 'exam-behandlungspflege-pilot') && (
              <button
                onClick={seedPilot}
                disabled={busy}
                className="px-5 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider border border-terra text-terra hover:bg-terra hover:text-cream-50 transition-colors disabled:opacity-50"
                data-testid="seed-pilot-btn"
              >
                Pilot: Behandlungspflege anlegen (20 Fragen)
              </button>
            )}
          </div>

          {exams === null && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-terra" /></div>}
          {exams && exams.length === 0 && (
            <p className="font-display italic text-[17px] text-sand-600 py-8" data-testid="exams-empty">Noch keine Prüfung angelegt.</p>
          )}

          <div className="space-y-4">
            {(exams || []).map((exam) => (
              <div key={exam.id} className="bg-cream-100 border border-cream-300 rounded-2xl" data-testid={`exam-row-${exam.id}`}>
                <div className="p-4 sm:p-5 flex flex-wrap items-center gap-3">
                  <button onClick={() => toggleExpand(exam.id)} className="flex-shrink-0" data-testid={`exam-expand-${exam.id}`}>
                    {expanded === exam.id ? <ChevronDown className="w-5 h-5 text-sand-500" /> : <ChevronRight className="w-5 h-5 text-sand-500" />}
                  </button>
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-[15px] font-semibold text-espresso">{exam.title}</p>
                    <p className="text-[12px] text-sand-600">
                      {exam.course_title} · Pool: {exam.pool_mc || 0} MC + {exam.pool_open || 0} offene · pro Versuch {exam.mc_per_attempt || 0} MC + {exam.open_per_attempt || 0} offene · {exam.duration_minutes} Min. · Bestehensgrenze über {exam.pass_percent || 50} %
                    </p>
                    <p className="text-[11.5px] text-sand-500 mt-0.5">
                      {Object.entries(exam.attempt_counts || {}).map(([k, v]) => `${v}× ${STATUS[k]?.label || k}`).join(' · ') || 'Noch keine Versuche'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => openUnlock(exam)} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider border border-terra text-terra hover:bg-terra hover:text-cream-50 transition-colors" data-testid={`unlock-exam-${exam.id}`}>
                      <Unlock className="w-3.5 h-3.5" /> Freischalten
                    </button>
                    <button
                      onClick={() => setBuilder({ ...exam, questions: (exam.question_pool || []).map(rowFromQuestion) })}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider border border-cream-400 text-sand-700 hover:bg-cream-200"
                      data-testid={`edit-exam-${exam.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" /> Bearbeiten
                    </button>
                  </div>
                </div>

                {expanded === exam.id && (
                  <div className="border-t border-cream-300 p-4 sm:p-5">
                    {!attempts[exam.id] && <Loader2 className="w-5 h-5 animate-spin text-terra" />}
                    {attempts[exam.id]?.length === 0 && <p className="text-[13px] text-sand-500">Noch keine Versuche.</p>}
                    <div className="space-y-2.5">
                      {(attempts[exam.id] || []).map((att) => {
                        const st = STATUS[att.status] || STATUS.freigeschaltet;
                        const fl = flagSum(att.flags);
                        return (
                          <div key={att.id} className={`flex flex-wrap items-center gap-3 p-3 rounded-xl bg-cream-50 border border-cream-300 ${att.superseded ? 'opacity-50' : ''}`} data-testid={`attempt-row-${att.id}`}>
                            <div className="flex-1 min-w-[160px]">
                              <p className="text-[13.5px] font-semibold text-espresso">
                                {att.user_name}
                                {att.superseded && <span className="ml-2 text-[10px] uppercase text-sand-500">(ersetzt)</span>}
                              </p>
                              <p className="text-[11.5px]" style={{ color: st.color }}>
                                {st.label}
                                {att.result?.outcome && ` — ${att.result.outcome === 'bestanden' ? 'Bestanden' : 'Nicht bestanden'}`}
                                {att.result?.percent !== undefined && att.result?.percent !== null && ` · ${att.result.percent} %`}
                                {att.status === 'abgegeben' && att.scoring?.mc_max > 0 && ` · MC ${att.scoring.mc_points}/${att.scoring.mc_max} P. (auto)`}
                                {att.auto_submitted && ' · Auto-Abgabe'}
                                {!att.camera_required && ' · ohne Kamera'}
                              </p>
                              {fl > 0 && (
                                <p className="text-[11.5px] text-red-700 flex items-center gap-1 mt-0.5" data-testid={`attempt-flags-${att.id}`}>
                                  <AlertTriangle className="w-3 h-3" />
                                  {att.flags.tab_hidden > 0 && `${att.flags.tab_hidden}× Tab verlassen `}
                                  {att.flags.fullscreen_exit > 0 && `${att.flags.fullscreen_exit}× Vollbild verlassen `}
                                  {att.flags.paste_blocked > 0 && `${att.flags.paste_blocked}× Paste `}
                                  {att.flags.camera_off > 0 && `${att.flags.camera_off}× Kamera aus`}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {['abgegeben', 'bewertet'].includes(att.status) && (
                                <>
                                  <button onClick={() => downloadPdf(att)} className="p-2 rounded-lg border border-cream-400 text-sand-700 hover:bg-cream-200" title="Prüfung als PDF" data-testid={`attempt-pdf-${att.id}`}>
                                    <FileDown className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => downloadProtocol(att)} className="p-2 rounded-lg border border-cream-400 text-sand-700 hover:bg-cream-200" title="Aufsichtsprotokoll (Auffälligkeiten + Chat) als PDF" data-testid={`attempt-protocol-${att.id}`}>
                                    <ShieldCheck className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => setGradingId(att.id)} className="px-3.5 py-2 rounded-full text-[10.5px] font-bold uppercase tracking-wider" style={{ backgroundColor: '#2F7A3D', color: '#FBF7F0' }} data-testid={`attempt-review-${att.id}`}>
                                    Bewerten
                                  </button>
                                </>
                              )}
                              {['abgegeben', 'bewertet'].includes(att.status) && !att.superseded && (
                                <button onClick={() => allowRetry(att)} className="p-2 rounded-lg border border-cream-400 text-sand-700 hover:bg-cream-200" title="Wiederholung freigeben" data-testid={`attempt-retry-${att.id}`}>
                                  <RotateCcw className="w-4 h-4" />
                                </button>
                              )}
                              <button onClick={() => deleteAttempt(att)} className="p-2 rounded-lg border border-cream-400 text-red-700 hover:bg-red-50" title="Versuch löschen" data-testid={`attempt-delete-${att.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* ============ BUILDER-MODAL ============ */}
      {builder && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4 py-8 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="bg-cream-50 rounded-2xl p-6 max-w-2xl w-full my-auto" data-testid="exam-builder-modal">
            <h3 className="font-display text-xl text-espresso mb-4">{builder.id ? 'Prüfung bearbeiten' : 'Neue Prüfung'}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <select
                value={builder.course_id || ''}
                onChange={(e) => setBuilder((b) => ({ ...b, course_id: e.target.value }))}
                className="px-3 py-2.5 bg-white border border-cream-300 rounded-xl text-[13px]"
                data-testid="builder-course-select"
              >
                <option value="">Kurs wählen…</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
              <input
                value={builder.title || ''}
                onChange={(e) => setBuilder((b) => ({ ...b, title: e.target.value }))}
                placeholder="Titel der Prüfung"
                className="px-3 py-2.5 bg-white border border-cream-300 rounded-xl text-[13px]"
                data-testid="builder-title-input"
              />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <label className="text-[12px] text-sand-600">
                Zeitlimit (Min.)
                <input type="number" min={5} max={480} value={builder.duration_minutes} onChange={(e) => setBuilder((b) => ({ ...b, duration_minutes: e.target.value }))} className="mt-1 w-full px-3 py-2 bg-white border border-cream-300 rounded-xl text-[13px]" data-testid="builder-duration-input" />
              </label>
              <label className="text-[12px] text-sand-600">
                MC pro Versuch
                <input type="number" min={0} max={100} value={builder.mc_per_attempt} onChange={(e) => setBuilder((b) => ({ ...b, mc_per_attempt: e.target.value }))} className="mt-1 w-full px-3 py-2 bg-white border border-cream-300 rounded-xl text-[13px]" data-testid="builder-mc-count" />
              </label>
              <label className="text-[12px] text-sand-600">
                Offene pro Versuch
                <input type="number" min={0} max={100} value={builder.open_per_attempt} onChange={(e) => setBuilder((b) => ({ ...b, open_per_attempt: e.target.value }))} className="mt-1 w-full px-3 py-2 bg-white border border-cream-300 rounded-xl text-[13px]" data-testid="builder-open-count" />
              </label>
              <label className="text-[12px] text-sand-600">
                Bestehen über (%)
                <input type="number" min={1} max={99} value={builder.pass_percent} onChange={(e) => setBuilder((b) => ({ ...b, pass_percent: e.target.value }))} className="mt-1 w-full px-3 py-2 bg-white border border-cream-300 rounded-xl text-[13px]" data-testid="builder-pass-percent" />
              </label>
            </div>

            {/* Fragen-Editor */}
            <div className="flex items-center justify-between mb-2">
              <p className="text-[12px] font-semibold text-sand-700">
                Fragen-Pool ({(builder.questions || []).filter((q) => q.type !== 'open' && (q.text || '').trim()).length} MC · {(builder.questions || []).filter((q) => q.type === 'open' && (q.text || '').trim()).length} offene)
              </p>
              <div className="flex gap-2">
                <button onClick={() => setBuilder((b) => ({ ...b, questions: [...(b.questions || []), { type: 'mc', text: '', points: 2, options_text: '' }] }))} className="px-3 py-1.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider border border-terra text-terra" data-testid="builder-add-mc">+ MC-Frage</button>
                <button onClick={() => setBuilder((b) => ({ ...b, questions: [...(b.questions || []), { type: 'open', text: '', points: 5, options_text: '' }] }))} className="px-3 py-1.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider border border-cream-400 text-sand-700" data-testid="builder-add-open">+ Offene Frage</button>
              </div>
            </div>
            <div className="max-h-[42vh] overflow-y-auto space-y-3 pr-1">
              {(builder.questions || []).length === 0 && (
                <p className="text-[12.5px] italic text-sand-500 py-4">Noch keine Fragen — oben MC- oder offene Fragen hinzufügen.</p>
              )}
              {(builder.questions || []).map((row, idx) => (
                <div key={idx} className="bg-cream-100 border border-cream-300 rounded-xl p-3" data-testid={`builder-q-${idx + 1}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] font-bold text-sand-500 w-6">{idx + 1}.</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${row.type !== 'open' ? 'bg-terra/10 text-terra' : 'bg-cream-300 text-sand-700'}`}>{row.type === 'mc_multi' ? 'MC mehrfach' : row.type === 'mc' ? 'MC' : 'Offen'}</span>
                    <label className="flex items-center gap-1 text-[11.5px] text-sand-600 ml-auto">
                      Punkte
                      <input type="number" min={1} max={100} value={row.points}
                        onChange={(e) => setBuilder((b) => { const qs = [...b.questions]; qs[idx] = { ...qs[idx], points: e.target.value }; return { ...b, questions: qs }; })}
                        className="w-16 px-2 py-1 bg-white border border-cream-300 rounded-lg text-[12px]" data-testid={`builder-q-points-${idx + 1}`} />
                    </label>
                    <button onClick={() => setBuilder((b) => ({ ...b, questions: b.questions.filter((_, i) => i !== idx) }))} className="text-red-700 text-[11px] font-bold" data-testid={`builder-q-remove-${idx + 1}`}>Entfernen</button>
                  </div>
                  <textarea
                    value={row.text}
                    onChange={(e) => setBuilder((b) => { const qs = [...b.questions]; qs[idx] = { ...qs[idx], text: e.target.value }; return { ...b, questions: qs }; })}
                    rows={2}
                    placeholder="Fragetext…"
                    className="w-full px-3 py-2 bg-white border border-cream-300 rounded-xl text-[13px] resize-y"
                    data-testid={`builder-q-text-${idx + 1}`}
                  />
                  {row.type !== 'open' && (
                    <textarea
                      value={row.options_text}
                      onChange={(e) => setBuilder((b) => { const qs = [...b.questions]; qs[idx] = { ...qs[idx], options_text: e.target.value }; return { ...b, questions: qs }; })}
                      rows={4}
                      placeholder={'Antwortoptionen — eine pro Zeile, richtige mit * markieren (mehrere * = Mehrfachauswahl):\nOption A\n*Option B (richtig)\nOption C'}
                      className="mt-2 w-full px-3 py-2 bg-white border border-cream-300 rounded-xl text-[12.5px] leading-relaxed resize-y font-mono"
                      data-testid={`builder-q-options-${idx + 1}`}
                    />
                  )}
                  {row.type === 'open' && (
                    <textarea
                      value={row.sample_solution || ''}
                      onChange={(e) => setBuilder((b) => { const qs = [...b.questions]; qs[idx] = { ...qs[idx], sample_solution: e.target.value }; return { ...b, questions: qs }; })}
                      rows={2}
                      placeholder="Musterlösung (optional — wird dem Dozenten und der KI-Vorbewertung angezeigt, nie dem Teilnehmer)…"
                      className="mt-2 w-full px-3 py-2 bg-white border border-cream-300 rounded-xl text-[12.5px] leading-relaxed resize-y"
                      data-testid={`builder-q-solution-${idx + 1}`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center mt-4">
              {builder.id ? (
                <button
                  onClick={async () => {
                    if (!window.confirm('Prüfung wirklich löschen?')) return;
                    try { await axios.delete(`${API}/admin/exams/${builder.id}`, getAuthHeader()); toast.success('Gelöscht'); setBuilder(null); load(); }
                    catch (e) { toast.error(e.response?.data?.detail || 'Fehlgeschlagen'); }
                  }}
                  className="text-[11px] font-bold uppercase tracking-wider text-red-700"
                  data-testid="builder-delete-btn"
                >
                  Löschen
                </button>
              ) : <span />}
              <div className="flex gap-3">
                <button onClick={() => setBuilder(null)} className="px-5 py-2.5 rounded-full border border-cream-400 text-sand-700 text-[11px] font-bold uppercase tracking-wider" data-testid="builder-cancel">Abbrechen</button>
                <button onClick={saveExam} disabled={busy} className="px-5 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider disabled:opacity-50" style={{ backgroundColor: TERRA, color: '#FBF7F0' }} data-testid="builder-save">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ FREISCHALT-MODAL ============ */}
      {candidates && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4 py-8" role="dialog" aria-modal="true">
          <div className="bg-cream-50 rounded-2xl p-6 max-w-lg w-full max-h-[80vh] flex flex-col" data-testid="unlock-modal">
            <h3 className="font-display text-xl text-espresso mb-1">Teilnehmer freischalten</h3>
            <p className="text-[12.5px] text-sand-600 mb-4">Standardmäßig mit Kamera-Pflicht. „Ohne Kamera" nur im Notfall (Teilnehmer filmt sich parallel mit dem Handy).</p>
            <div className="flex-1 overflow-y-auto space-y-2">
              {candidates.list === null && <Loader2 className="w-5 h-5 animate-spin text-terra" />}
              {candidates.list?.length === 0 && <p className="text-[13px] text-sand-500">Keine Teilnehmer im Kurs.</p>}
              {(candidates.list || []).map((u) => (
                <div key={u.id} className="flex items-center gap-3 p-3 bg-cream-100 border border-cream-300 rounded-xl" data-testid={`candidate-${u.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] font-semibold text-espresso truncate">{u.name || u.username}</p>
                    {u.attempt_status && <p className="text-[11px]" style={{ color: (STATUS[u.attempt_status] || {}).color }}>{(STATUS[u.attempt_status] || {}).label}</p>}
                  </div>
                  {!u.attempt_status ? (
                    <div className="flex gap-1.5">
                      <button onClick={() => unlock(u.id, true)} className="px-3.5 py-2 rounded-full text-[10.5px] font-bold uppercase tracking-wider" style={{ backgroundColor: TERRA, color: '#FBF7F0' }} data-testid={`unlock-cam-${u.id}`}>
                        <Camera className="w-3 h-3 inline mr-1 -mt-0.5" />Mit Kamera
                      </button>
                      <button onClick={() => unlock(u.id, false)} className="px-3.5 py-2 rounded-full text-[10.5px] font-bold uppercase tracking-wider border border-cream-400 text-sand-600 hover:bg-cream-200" data-testid={`unlock-nocam-${u.id}`}>
                        Ohne
                      </button>
                    </div>
                  ) : <CheckCircle2 className="w-4 h-4 text-sand-400" />}
                </div>
              ))}
            </div>
            <button onClick={() => setCandidates(null)} className="mt-4 px-5 py-2.5 rounded-full border border-cream-400 text-sand-700 text-[11px] font-bold uppercase tracking-wider self-end" data-testid="unlock-close">Schließen</button>
          </div>
        </div>
      )}

      {/* ============ BEWERTUNGS-MODAL ============ */}
      {gradingId && (
        <ExamGradingModal
          attemptId={gradingId}
          getAuthHeader={getAuthHeader}
          onClose={() => setGradingId(null)}
          onSaved={() => { loadAttempts(expanded); load(); }}
        />
      )}
    </div>
  );

  return Layout ? <Layout>{content}</Layout> : content;
}
